import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import { toast } from 'react-toastify';
import api from '../../services/api';
import adminService from '../../services/adminService';
import couponService from '../../services/couponService';
import {
    FaTicketAlt,
    FaPercent,
    FaDollarSign,
    FaCalendarAlt,
    FaHashtag,
    FaInfoCircle,
    FaPlus,
    FaWhatsapp,
    FaPaperPlane,
    FaComments,
    FaCheck,
    FaTimes,
    FaSync,
    FaSearch,
    FaBuilding,
    FaChartBar,
    FaPlay,
    FaUserClock,
    FaEnvelopeOpenText
} from 'react-icons/fa';

import './style.css';

const AdminDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [couponForm, setCouponForm] = useState({
        code: '',
        description: '',
        discount_type: 'percent',
        discount_value: '',
        max_uses: '',
        expires_at: '',
        is_active: true
    });

    const [activeTab, setActiveTab] = useState('overview');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [consentRequired, setConsentRequired] = useState(false);

    // WhatsApp state
    const [whatsappStatus, setWhatsappStatus] = useState(null);
    const [whatsappChats, setWhatsappChats] = useState([]);
    const [whatsappChatPage, setWhatsappChatPage] = useState(1);
    const [whatsappTotalChats, setWhatsappTotalChats] = useState(0);
    const [whatsappChatSearch, setWhatsappChatSearch] = useState('');
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [messagePhone, setMessagePhone] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // WhatsApp config state
    const [whatsappConfig, setWhatsappConfig] = useState({
        api_url: '',
        api_key: '',
        instance_id: '',
        default_phone: ''
    });
    const [savingConfig, setSavingConfig] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);

    // Institution state
    const [institutions, setInstitutions] = useState([]);
    const [institutionForm, setInstitutionForm] = useState({
        name: '',
        code: '',
        admin_email: '',
        admin_password: '',
        admin_first_name: '',
        admin_last_name: ''
    });

    // Bulk coupon state
    const [bulkCouponForm, setBulkCouponForm] = useState({
        prefix: '',
        count: 10,
        institution_id: '',
        description: '',
        discount_type: 'percent',
        discount_value: '',
        max_uses: 1,
        expires_at: ''
    });
    const [bulkCouponResult, setBulkCouponResult] = useState(null);

    // Analytics state
    const [analyticsData, setAnalyticsData] = useState(null);
    const [campaignHistory, setCampaignHistory] = useState([]);
    const [campaignHistoryTotal, setCampaignHistoryTotal] = useState(0);
    const [campaignHistoryPage, setCampaignHistoryPage] = useState(1);
    const [campaignFilter, setCampaignFilter] = useState('');
    const [userLookupId, setUserLookupId] = useState('');
    const [userAnalytics, setUserAnalytics] = useState(null);
    const [runningCampaigns, setRunningCampaigns] = useState(false);
    const [campaignRunResult, setCampaignRunResult] = useState(null);

    // Create User State
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [createUserForm, setCreateUserForm] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        subscription_type: 'none', // none, demo, paid
        subscription_months: 12
    });

    useEffect(() => {
        // Check if user is admin
        if (user && !user.is_admin) {
            toast.error('Access denied. Admin privileges required.');
            navigate('/dashboard');
        }
    }, [user, navigate]);

    useEffect(() => {
        if (user && user.is_admin) {
            loadData();
        }
    }, [user, activeTab, page, searchTerm]);

    // Add a small delay to ensure proper initialization
    useEffect(() => {
        if (!user) {
            setLoading(true);
        }
    }, [user]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Ensure we have a valid token before making requests
            const token = localStorage.getItem('access_token');
            if (!token) {
                toast.error('Authentication required');
                navigate('/login');
                return;
            }

            if (activeTab === 'overview') {
                const response = await api.get('/admin/stats');
                setStats(response.data);
            } else if (activeTab === 'users') {
                const response = await api.get('/admin/users', {
                    params: { page, page_size: 20, search: searchTerm || undefined }
                });
                setUsers(response.data.users);
                setTotalUsers(response.data.total);
            } else if (activeTab === 'subscriptions') {
                const response = await api.get('/admin/subscriptions');
                setSubscriptions(response.data);
            } else if (activeTab === 'coupons') {
                const data = await couponService.getCoupons();
                setCoupons(data);
            } else if (activeTab === 'settings') {
                const settings = await adminService.getSettings();
                setConsentRequired(settings.consent_required);
            } else if (activeTab === 'whatsapp') {
                const statusData = await adminService.getWhatsAppStatus();
                setWhatsappStatus(statusData);

                const chatsData = await adminService.getWhatsAppChats({
                    page: whatsappChatPage,
                    page_size: 20,
                    search: whatsappChatSearch || undefined
                });
                setWhatsappChats(chatsData.chats || []);
                setWhatsappTotalChats(chatsData.total || 0);

                // Load WhatsApp config
                const configData = await adminService.getWhatsAppConfig();
                setWhatsappConfig({
                    api_url: configData.api_url || '',
                    api_key: configData.api_key || '',
                    instance_id: configData.instance_id || '',
                    default_phone: configData.default_phone || ''
                });
            } else if (activeTab === 'institutions') {
                const institutionsData = await adminService.getInstitutions();
                setInstitutions(institutionsData);
            } else if (activeTab === 'analytics') {
                const dashData = await adminService.getAnalyticsDashboard();
                setAnalyticsData(dashData);
                const histData = await adminService.getCampaignHistory({
                    page: campaignHistoryPage,
                    page_size: 20,
                    campaign_id: campaignFilter || undefined
                });
                setCampaignHistory(histData.campaigns || []);
                setCampaignHistoryTotal(histData.total || 0);
            }

        } catch (error) {
            const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to load data';
            toast.error(errorMessage);
            console.error('Admin data load error:', error);

            // If it's a 403, redirect to dashboard
            if (error.response?.status === 403) {
                toast.error('Admin access required');
                navigate('/dashboard');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConsentToggle = async () => {
        try {
            const updated = await adminService.updateSettings({ consent_required: !consentRequired });
            setConsentRequired(updated.consent_required);
            toast.success('Consent setting updated');
        } catch (error) {
            toast.error('Failed to update consent setting');
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        try {
            await api.patch(`/admin/users/${userId}`, {
                is_active: !currentStatus
            });
            toast.success('User status updated');
            loadData();
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    const handleToggleAdmin = async (userId, currentStatus) => {
        try {
            await api.patch(`/admin/users/${userId}`, {
                is_admin: !currentStatus
            });
            toast.success('Admin status updated');
            loadData();
        } catch (error) {
            toast.error('Failed to update admin status');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }
        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to delete user');
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();

        // Simple validation
        if (!createUserForm.email || !createUserForm.password || !createUserForm.first_name || !createUserForm.last_name) {
            toast.error('Please fill all required fields');
            return;
        }

        if (createUserForm.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        try {
            const data = {
                ...createUserForm,
                subscription_type: createUserForm.subscription_type === 'none' ? null : createUserForm.subscription_type,
                subscription_months: parseInt(createUserForm.subscription_months)
            };

            await adminService.createUser(data);
            toast.success('User created successfully');
            setShowCreateUser(false);
            setCreateUserForm({
                email: '',
                password: '',
                first_name: '',
                last_name: '',
                subscription_type: 'none',
                subscription_months: 12
            });
            loadData();
        } catch (error) {
            const msg = error.response?.data?.error?.message || 'Failed to create user';
            toast.error(msg);
        }
    };

    const handleCreateUserChange = (e) => {
        const { name, value } = e.target;
        setCreateUserForm(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateSubscription = async (subscriptionId, status) => {
        try {
            await api.patch(`/admin/subscriptions/${subscriptionId}`, { status });
            toast.success('Subscription updated');
            loadData();
        } catch (error) {
            toast.error('Failed to update subscription');
        }
    };

    const handleExpireSubscription = async (subscriptionId) => {
        if (!window.confirm('Are you sure you want to expire this subscription? This is for testing purposes.')) {
            return;
        }
        try {
            await adminService.expireSubscription(subscriptionId);
            toast.success('Subscription expired successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to expire subscription');
        }
    };

    const handleCouponFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCouponForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCreateCoupon = async (e) => {
        e.preventDefault();

        // Validation
        if (!couponForm.code.trim()) {
            toast.error('Coupon code is required');
            return;
        }
        if (!couponForm.discount_value || parseFloat(couponForm.discount_value) <= 0) {
            toast.error('Discount value must be greater than 0');
            return;
        }
        if (couponForm.discount_type === 'percent' && parseFloat(couponForm.discount_value) > 100) {
            toast.error('Percentage discount cannot exceed 100%');
            return;
        }

        const data = {
            code: couponForm.code.trim().toUpperCase(),
            description: couponForm.description.trim() || null,
            discount_type: couponForm.discount_type,
            discount_value: parseFloat(couponForm.discount_value),
            max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
            expires_at: couponForm.expires_at ? new Date(couponForm.expires_at).toISOString() : null,
            is_active: couponForm.is_active
        };

        try {
            await couponService.createCoupon(data);
            toast.success('Coupon created successfully!');
            loadData();
            // Reset form
            setCouponForm({
                code: '',
                description: '',
                discount_type: 'percent',
                discount_value: '',
                max_uses: '',
                expires_at: '',
                is_active: true
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create coupon');
        }
    };

    // ==========================================
    // Institution Handlers
    // ==========================================

    const handleInstitutionFormChange = (e) => {
        const { name, value } = e.target;
        setInstitutionForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateInstitution = async (e) => {
        e.preventDefault();

        // Validation
        if (!institutionForm.name.trim()) {
            toast.error('Institution name is required');
            return;
        }
        if (!institutionForm.admin_email.trim()) {
            toast.error('Admin email is required');
            return;
        }
        if (!institutionForm.admin_password || institutionForm.admin_password.length < 6) {
            toast.error('Admin password must be at least 6 characters');
            return;
        }

        try {
            await adminService.createInstitution(institutionForm);
            toast.success('Institution created successfully!');
            loadData();
            // Reset form
            setInstitutionForm({
                name: '',
                code: '',
                admin_email: '',
                admin_password: '',
                admin_first_name: '',
                admin_last_name: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create institution');
        }
    };

    // ==========================================
    // Bulk Coupon Handlers
    // ==========================================

    const handleBulkCouponFormChange = (e) => {
        const { name, value, type } = e.target;
        setBulkCouponForm(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? '' : parseInt(value)) : value
        }));
    };

    const handleCreateBulkCoupons = async (e) => {
        e.preventDefault();
        setBulkCouponResult(null);

        // Validation
        if (!bulkCouponForm.prefix.trim()) {
            toast.error('Coupon prefix is required');
            return;
        }
        if (!bulkCouponForm.institution_id) {
            toast.error('Please select an institution');
            return;
        }
        if (!bulkCouponForm.discount_value || parseFloat(bulkCouponForm.discount_value) <= 0) {
            toast.error('Discount value must be greater than 0');
            return;
        }

        const data = {
            prefix: bulkCouponForm.prefix.trim().toUpperCase(),
            count: bulkCouponForm.count || 10,
            institution_id: bulkCouponForm.institution_id,
            description: bulkCouponForm.description.trim() || null,
            discount_type: bulkCouponForm.discount_type,
            discount_value: parseFloat(bulkCouponForm.discount_value),
            max_uses: bulkCouponForm.max_uses || 1,
            expires_at: bulkCouponForm.expires_at ? new Date(bulkCouponForm.expires_at).toISOString() : null
        };

        try {
            const result = await adminService.createBulkCoupons(data);
            toast.success(`Created ${result.created_count} coupons successfully!`);
            setBulkCouponResult(result);
            // Reset form
            setBulkCouponForm({
                prefix: '',
                count: 10,
                institution_id: '',
                description: '',
                discount_type: 'percent',
                discount_value: '',
                max_uses: 1,
                expires_at: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create bulk coupons');
        }
    };


    if (!user || !user.is_admin) {
        return null;
    }

    return (
        <div className="admin-dashboard-page">
            <div className="admin-dashboard-wrapper">
                <InternalNavbar />
                <div className="admin-dashboard-container">
                    <div className="admin-header">
                        <h1>Admin Dashboard</h1>
                        <p>Manage users, subscriptions, and platform settings</p>
                    </div>

                    <div className="admin-tabs">
                        <button
                            className={activeTab === 'overview' ? 'active' : ''}
                            onClick={() => setActiveTab('overview')}
                        >
                            Overview
                        </button>
                        <button
                            className={activeTab === 'users' ? 'active' : ''}
                            onClick={() => setActiveTab('users')}
                        >
                            Users
                        </button>
                        <button
                            className={activeTab === 'subscriptions' ? 'active' : ''}
                            onClick={() => setActiveTab('subscriptions')}
                        >
                            Subscriptions
                        </button>
                        <button
                            className={activeTab === 'coupons' ? 'active' : ''}
                            onClick={() => setActiveTab('coupons')}
                        >
                            Coupons
                        </button>
                        <button
                            className={activeTab === 'settings' ? 'active' : ''}
                            onClick={() => setActiveTab('settings')}
                        >
                            Settings
                        </button>
                        <button
                            className={activeTab === 'whatsapp' ? 'active' : ''}
                            onClick={() => setActiveTab('whatsapp')}
                        >
                            <FaWhatsapp style={{ marginRight: '6px' }} /> WhatsApp
                        </button>
                        <button
                            className={activeTab === 'institutions' ? 'active' : ''}
                            onClick={() => setActiveTab('institutions')}
                        >
                            <FaBuilding style={{ marginRight: '6px' }} /> Institutions
                        </button>
                        <button
                            className={activeTab === 'analytics' ? 'active' : ''}
                            onClick={() => setActiveTab('analytics')}
                        >
                            <FaChartBar style={{ marginRight: '6px' }} /> Analytics
                        </button>

                    </div>

                    {loading ? (
                        <div className="admin-loading">Loading...</div>
                    ) : (
                        <>
                            {activeTab === 'overview' && stats && (
                                <div className="admin-overview">
                                    <div className="stats-grid">
                                        <div className="stat-card">
                                            <h3>Total Users</h3>
                                            <p className="stat-number">{stats.users.total}</p>
                                            <div className="stat-details">
                                                <span>Active: {stats.users.active}</span>
                                                <span>Verified: {stats.users.verified}</span>
                                            </div>
                                        </div>
                                        <div className="stat-card">
                                            <h3>Subscriptions</h3>
                                            <p className="stat-number">{stats.subscriptions.total}</p>
                                            <div className="stat-details">
                                                <span>Active: {stats.subscriptions.active}</span>
                                            </div>
                                        </div>
                                        <div className="stat-card">
                                            <h3>Demo Accounts</h3>
                                            <p className="stat-number">{stats.subscriptions.demo}</p>
                                        </div>
                                        <div className="stat-card">
                                            <h3>Paid Subscriptions</h3>
                                            <p className="stat-number">{stats.subscriptions.paid}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'users' && (
                                <div className="admin-users">
                                    <div className="users-header">
                                        <div className="users-actions">
                                            <input
                                                type="text"
                                                placeholder="Search users..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="search-input"
                                            />
                                            <button
                                                className="btn-primary"
                                                onClick={() => setShowCreateUser(!showCreateUser)}
                                            >
                                                <FaPlus /> Create New User
                                            </button>
                                        </div>
                                        <span className="user-count">Total: {totalUsers} users</span>
                                    </div>

                                    {showCreateUser && (
                                        <div className="create-user-section card">
                                            <h3>Create New User</h3>
                                            <form onSubmit={handleCreateUser} className="create-user-form">
                                                <div className="form-row">
                                                    <div className="form-group-enhanced">
                                                        <label>First Name *</label>
                                                        <input
                                                            name="first_name"
                                                            value={createUserForm.first_name}
                                                            onChange={handleCreateUserChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="form-group-enhanced">
                                                        <label>Last Name *</label>
                                                        <input
                                                            name="last_name"
                                                            value={createUserForm.last_name}
                                                            onChange={handleCreateUserChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group-enhanced">
                                                        <label>Email *</label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={createUserForm.email}
                                                            onChange={handleCreateUserChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="form-group-enhanced">
                                                        <label>Password *</label>
                                                        <input
                                                            type="password"
                                                            name="password"
                                                            value={createUserForm.password}
                                                            onChange={handleCreateUserChange}
                                                            required
                                                            placeholder="Min 6 characters"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group-enhanced">
                                                        <label>Subscription Type</label>
                                                        <select
                                                            name="subscription_type"
                                                            value={createUserForm.subscription_type}
                                                            onChange={handleCreateUserChange}
                                                        >
                                                            <option value="none">None</option>
                                                            <option value="demo">Demo (2 weeks)</option>
                                                            <option value="paid">Paid (Premium)</option>
                                                        </select>
                                                    </div>
                                                    {createUserForm.subscription_type === 'paid' && (
                                                        <div className="form-group-enhanced">
                                                            <label>Duration (Months)</label>
                                                            <input
                                                                type="number"
                                                                name="subscription_months"
                                                                value={createUserForm.subscription_months}
                                                                onChange={handleCreateUserChange}
                                                                min="1"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="form-actions right">
                                                    <button type="button" onClick={() => setShowCreateUser(false)} className="btn-text">
                                                        Cancel
                                                    </button>
                                                    <button type="submit" className="btn-primary">
                                                        Create User
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                    <div className="users-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Email</th>
                                                    <th>Name</th>
                                                    <th>Status</th>
                                                    <th>Admin</th>
                                                    <th>Verified</th>
                                                    <th>Created</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map(u => (
                                                    <tr key={u.id}>
                                                        <td>{u.email}</td>
                                                        <td>{u.first_name} {u.last_name}</td>
                                                        <td>
                                                            <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                                                                {u.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${u.is_admin ? 'admin' : ''}`}>
                                                                {u.is_admin ? 'Yes' : 'No'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${u.is_verified ? 'verified' : ''}`}>
                                                                {u.is_verified ? 'Yes' : 'No'}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                                                        <td className="actions">
                                                            <button
                                                                onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                                                                className="btn-small"
                                                            >
                                                                {u.is_active ? 'Deactivate' : 'Activate'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                                                                className="btn-small"
                                                            >
                                                                {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteUser(u.id)}
                                                                className="btn-small btn-danger"
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="pagination">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>
                                        <span>Page {page}</span>
                                        <button
                                            onClick={() => setPage(p => p + 1)}
                                            disabled={users.length < 20}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'subscriptions' && (
                                <div className="admin-subscriptions">
                                    <div className="subscriptions-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>User</th>
                                                    <th>Email</th>
                                                    <th>Type</th>
                                                    <th>Status</th>
                                                    <th>Start Date</th>
                                                    <th>End Date</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subscriptions.map(item => (
                                                    <tr key={item.subscription.id}>
                                                        <td>{item.user.first_name} {item.user.last_name}</td>
                                                        <td>{item.user.email}</td>
                                                        <td>
                                                            <span className={`type-badge ${item.subscription.type}`}>
                                                                {item.subscription.type}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${item.subscription.status}`}>
                                                                {item.subscription.status}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(item.subscription.start_date).toLocaleDateString()}</td>
                                                        <td>{new Date(item.subscription.end_date).toLocaleDateString()}</td>
                                                        <td className="actions">
                                                            {item.subscription.status === 'active' ? (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleExpireSubscription(item.subscription.id)}
                                                                        className="btn-small btn-warning"
                                                                        title="Expire subscription for testing"
                                                                    >
                                                                        Expire
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleUpdateSubscription(item.subscription.id, 'cancelled')}
                                                                        className="btn-small btn-danger"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleUpdateSubscription(item.subscription.id, 'active')}
                                                                    className="btn-small"
                                                                >
                                                                    Activate
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'coupons' && (
                                <div className="admin-coupons">
                                    <div className="create-coupon-section card">
                                        <div className="section-header">
                                            <h3><FaTicketAlt /> Create New Coupon</h3>
                                            <p className="section-subtitle">Generate discount codes for your customers</p>
                                        </div>

                                        <form onSubmit={handleCreateCoupon} className="coupon-form">
                                            {/* Coupon Code */}
                                            <div className="form-row">
                                                <div className="form-group-enhanced full-width">
                                                    <label htmlFor="coupon-code">
                                                        <FaTicketAlt className="label-icon" />
                                                        Coupon Code *
                                                    </label>
                                                    <input
                                                        id="coupon-code"
                                                        name="code"
                                                        value={couponForm.code}
                                                        onChange={handleCouponFormChange}
                                                        placeholder="e.g., SAVE20, WELCOME50"
                                                        required
                                                        className="input-uppercase"
                                                    />
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Use uppercase letters and numbers (will be auto-formatted)
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <div className="form-row">
                                                <div className="form-group-enhanced full-width">
                                                    <label htmlFor="coupon-description">
                                                        Description
                                                    </label>
                                                    <input
                                                        id="coupon-description"
                                                        name="description"
                                                        value={couponForm.description}
                                                        onChange={handleCouponFormChange}
                                                        placeholder="e.g., New customer welcome discount"
                                                    />
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Optional: Help identify this coupon in the admin panel
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Discount Type & Value */}
                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="discount-type">
                                                        Discount Type *
                                                    </label>
                                                    <div className="select-wrapper">
                                                        <select
                                                            id="discount-type"
                                                            name="discount_type"
                                                            value={couponForm.discount_type}
                                                            onChange={handleCouponFormChange}
                                                        >
                                                            <option value="percent">Percentage (%)</option>
                                                            <option value="fixed_amount">Fixed Amount (€)</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group-enhanced">
                                                    <label htmlFor="discount-value">
                                                        {couponForm.discount_type === 'percent' ? (
                                                            <><FaPercent className="label-icon" /> Discount Percentage *</>
                                                        ) : (
                                                            <><FaDollarSign className="label-icon" /> Discount Amount *</>
                                                        )}
                                                    </label>
                                                    <div className="input-with-prefix">
                                                        {couponForm.discount_type === 'fixed_amount' && <span className="prefix">€</span>}
                                                        <input
                                                            id="discount-value"
                                                            name="discount_value"
                                                            type="number"
                                                            value={couponForm.discount_value}
                                                            onChange={handleCouponFormChange}
                                                            placeholder={couponForm.discount_type === 'percent' ? '10' : '5.00'}
                                                            step="0.01"
                                                            min="0.01"
                                                            max={couponForm.discount_type === 'percent' ? '100' : undefined}
                                                            required
                                                        />
                                                        {couponForm.discount_type === 'percent' && <span className="suffix">%</span>}
                                                    </div>
                                                    <small className="field-hint">
                                                        {couponForm.discount_type === 'percent'
                                                            ? 'Maximum 100%'
                                                            : 'Amount in Euros (€)'}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Max Uses & Expiry Date */}
                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="max-uses">
                                                        <FaHashtag className="label-icon" />
                                                        Maximum Uses
                                                    </label>
                                                    <input
                                                        id="max-uses"
                                                        name="max_uses"
                                                        type="number"
                                                        value={couponForm.max_uses}
                                                        onChange={handleCouponFormChange}
                                                        placeholder="Unlimited"
                                                        min="1"
                                                    />
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Leave empty for unlimited uses
                                                    </small>
                                                </div>

                                                <div className="form-group-enhanced">
                                                    <label htmlFor="expires-at">
                                                        <FaCalendarAlt className="label-icon" />
                                                        Expiration Date
                                                    </label>
                                                    <input
                                                        id="expires-at"
                                                        name="expires_at"
                                                        type="datetime-local"
                                                        value={couponForm.expires_at}
                                                        onChange={handleCouponFormChange}
                                                    />
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Leave empty for no expiration
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Active Status Toggle */}
                                            <div className="form-row">
                                                <div className="form-group-enhanced full-width">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            name="is_active"
                                                            checked={couponForm.is_active}
                                                            onChange={handleCouponFormChange}
                                                        />
                                                        <span className="checkbox-text">
                                                            Activate coupon immediately
                                                        </span>
                                                    </label>
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Inactive coupons cannot be redeemed
                                                    </small>
                                                </div>
                                            </div>

                                            <button type="submit" className="btn-create-coupon">
                                                <FaPlus /> Create Coupon
                                            </button>
                                        </form>
                                    </div>

                                    <div className="coupons-table-container card">
                                        <h3>All Coupons</h3>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Code</th>
                                                    <th>Description</th>
                                                    <th>Discount</th>
                                                    <th>Usage</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {coupons.map(coupon => (
                                                    <tr key={coupon.id}>
                                                        <td><strong>{coupon.code}</strong></td>
                                                        <td>{coupon.description}</td>
                                                        <td>
                                                            {coupon.discount_type === 'percent'
                                                                ? `${coupon.discount_value}%`
                                                                : `$${coupon.discount_value}`}
                                                        </td>
                                                        <td>
                                                            {coupon.current_uses}
                                                            {coupon.max_uses ? ` / ${coupon.max_uses}` : ''}
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${coupon.is_active ? 'active' : 'inactive'}`}>
                                                                {coupon.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {coupons.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">No coupons found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'settings' && (
                                <div className="admin-settings">
                                    <h2>Privacy & Consent</h2>
                                    <p>Control whether a cookie/analytics consent banner is shown to users.</p>
                                    <div className="setting-item">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={consentRequired}
                                                onChange={handleConsentToggle}
                                            />
                                            Require consent for analytics cookies
                                        </label>
                                        <p className="setting-help">
                                            When enabled, new users will see a consent banner. Analytics will only run after they accept.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'whatsapp' && (
                                <div className="admin-whatsapp">
                                    {/* Status Panel */}
                                    <div className="whatsapp-status-panel card">
                                        <div className="section-header">
                                            <h3><FaWhatsapp /> WhatsApp Server Status</h3>
                                            <button
                                                className="btn-small btn-refresh"
                                                onClick={() => loadData()}
                                                title="Refresh status"
                                            >
                                                <FaSync />
                                            </button>
                                        </div>
                                        {whatsappStatus && (
                                            <div className="status-content">
                                                <div className={`status-indicator ${whatsappStatus.connected ? 'connected' : 'disconnected'}`}>
                                                    {whatsappStatus.connected ? <FaCheck /> : <FaTimes />}
                                                    <span>{whatsappStatus.connected ? 'Connected' : 'Disconnected'}</span>
                                                </div>
                                                <div className="status-details">
                                                    <span><strong>Instance ID:</strong> {whatsappStatus.instance_id}</span>
                                                    <span><strong>Status:</strong> {whatsappStatus.status}</span>
                                                    <span><strong>Message:</strong> {whatsappStatus.message}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Configuration Panel */}
                                    <div className="whatsapp-config-panel card">
                                        <div className="section-header">
                                            <h3><FaTicketAlt /> API Configuration</h3>
                                        </div>
                                        <p className="section-subtitle">
                                            Configure WhatsApp API connection. Uses X-API-Key header for authentication.
                                        </p>
                                        <div className="config-form">
                                            <div className="config-row">
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="wa-api-url">API Endpoint URL</label>
                                                    <input
                                                        id="wa-api-url"
                                                        type="text"
                                                        placeholder="https://whatsapp-api.anasarabic.com/api/v1"
                                                        value={whatsappConfig.api_url}
                                                        onChange={(e) => setWhatsappConfig({
                                                            ...whatsappConfig,
                                                            api_url: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="wa-instance-id">Instance ID</label>
                                                    <input
                                                        id="wa-instance-id"
                                                        type="text"
                                                        placeholder="your-instance-id"
                                                        value={whatsappConfig.instance_id}
                                                        onChange={(e) => setWhatsappConfig({
                                                            ...whatsappConfig,
                                                            instance_id: e.target.value
                                                        })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="config-row">
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="wa-api-key">API Key (X-API-Key)</label>
                                                    <div className="api-key-input-wrapper">
                                                        <input
                                                            id="wa-api-key"
                                                            type={showApiKey ? "text" : "password"}
                                                            placeholder="wa_xxxxx..."
                                                            value={whatsappConfig.api_key}
                                                            onChange={(e) => setWhatsappConfig({
                                                                ...whatsappConfig,
                                                                api_key: e.target.value
                                                            })}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn-toggle-visibility"
                                                            onClick={() => setShowApiKey(!showApiKey)}
                                                        >
                                                            {showApiKey ? 'Hide' : 'Show'}
                                                        </button>
                                                    </div>
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Generate key from WhatsApp API dashboard
                                                    </small>
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="wa-default-phone">Default Phone Number</label>
                                                    <input
                                                        id="wa-default-phone"
                                                        type="text"
                                                        placeholder="+33612345678"
                                                        value={whatsappConfig.default_phone}
                                                        onChange={(e) => setWhatsappConfig({
                                                            ...whatsappConfig,
                                                            default_phone: e.target.value
                                                        })}
                                                    />
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Pre-fill phone in message composer
                                                    </small>
                                                </div>
                                            </div>
                                            <button
                                                className="btn-save-config"
                                                disabled={savingConfig}
                                                onClick={async () => {
                                                    setSavingConfig(true);
                                                    try {
                                                        await adminService.updateWhatsAppConfig(whatsappConfig);
                                                        toast.success('Configuration saved! Service reinitialized.');
                                                        // Refresh status to verify connection
                                                        loadData();
                                                    } catch (error) {
                                                        toast.error('Failed to save configuration');
                                                    } finally {
                                                        setSavingConfig(false);
                                                    }
                                                }}
                                            >
                                                {savingConfig ? 'Saving...' : (
                                                    <><FaCheck /> Save Configuration</>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="whatsapp-main-grid">
                                        {/* Message Composer */}
                                        <div className="whatsapp-composer card">
                                            <div className="section-header">
                                                <h3><FaPaperPlane /> Send Message</h3>
                                            </div>
                                            <div className="composer-form">
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="msg-phone">Phone Number</label>
                                                    <input
                                                        id="msg-phone"
                                                        type="text"
                                                        placeholder="e.g., +33612345678"
                                                        value={messagePhone}
                                                        onChange={(e) => setMessagePhone(e.target.value)}
                                                    />
                                                    <small className="field-hint">
                                                        <FaInfoCircle /> Include country code
                                                    </small>
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label htmlFor="msg-content">Message</label>
                                                    <textarea
                                                        id="msg-content"
                                                        placeholder="Type your message here..."
                                                        value={messageContent}
                                                        onChange={(e) => setMessageContent(e.target.value)}
                                                        rows={4}
                                                    />
                                                </div>
                                                <button
                                                    className="btn-send-message"
                                                    disabled={sendingMessage || !messagePhone || !messageContent}
                                                    onClick={async () => {
                                                        if (!messagePhone || !messageContent) return;
                                                        setSendingMessage(true);
                                                        try {
                                                            const result = await adminService.sendWhatsAppMessage(
                                                                messagePhone,
                                                                messageContent
                                                            );
                                                            if (result.success) {
                                                                toast.success('Message sent successfully!');
                                                                setMessageContent('');
                                                            } else {
                                                                toast.error(result.message || 'Failed to send message');
                                                            }
                                                        } catch (error) {
                                                            toast.error('Failed to send message');
                                                        } finally {
                                                            setSendingMessage(false);
                                                        }
                                                    }}
                                                >
                                                    {sendingMessage ? 'Sending...' : (
                                                        <><FaPaperPlane /> Send Message</>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Chat Viewer */}
                                        <div className="whatsapp-chats card">
                                            <div className="section-header">
                                                <h3><FaComments /> Chats ({whatsappTotalChats})</h3>
                                            </div>
                                            <div className="chats-search">
                                                <div className="search-input-wrapper">
                                                    <FaSearch className="search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search chats..."
                                                        value={whatsappChatSearch}
                                                        onChange={(e) => setWhatsappChatSearch(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                setWhatsappChatPage(1);
                                                                loadData();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="chats-list">
                                                {whatsappChats.length === 0 ? (
                                                    <div className="no-chats">No chats found</div>
                                                ) : (
                                                    whatsappChats.map((chat) => (
                                                        <div
                                                            key={chat.id}
                                                            className={`chat-item ${selectedChat?.id === chat.id ? 'selected' : ''}`}
                                                            onClick={async () => {
                                                                setSelectedChat(chat);
                                                                try {
                                                                    const messagesData = await adminService.getWhatsAppMessages(chat.id, 50);
                                                                    setChatMessages(messagesData.messages || []);
                                                                } catch (error) {
                                                                    toast.error('Failed to load messages');
                                                                    setChatMessages([]);
                                                                }
                                                            }}
                                                        >
                                                            <div className="chat-info">
                                                                <span className="chat-name">{chat.name || chat.id}</span>
                                                                {chat.timestamp && (
                                                                    <span className="chat-time">
                                                                        {new Date(chat.timestamp).toLocaleDateString()}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {chat.last_message && (
                                                                <span className="chat-preview">{chat.last_message}</span>
                                                            )}
                                                            {chat.unread_count > 0 && (
                                                                <span className="unread-badge">{chat.unread_count}</span>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            {whatsappTotalChats > 20 && (
                                                <div className="chats-pagination">
                                                    <button
                                                        disabled={whatsappChatPage === 1}
                                                        onClick={() => {
                                                            setWhatsappChatPage(p => Math.max(1, p - 1));
                                                            loadData();
                                                        }}
                                                    >
                                                        Previous
                                                    </button>
                                                    <span>Page {whatsappChatPage}</span>
                                                    <button
                                                        disabled={whatsappChats.length < 20}
                                                        onClick={() => {
                                                            setWhatsappChatPage(p => p + 1);
                                                            loadData();
                                                        }}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Selected Chat Messages */}
                                    {selectedChat && (
                                        <div className="whatsapp-messages card">
                                            <div className="section-header">
                                                <h3>
                                                    <FaComments /> {selectedChat.name || selectedChat.id}
                                                </h3>
                                                <button
                                                    className="btn-small"
                                                    onClick={() => {
                                                        setSelectedChat(null);
                                                        setChatMessages([]);
                                                    }}
                                                >
                                                    Close
                                                </button>
                                            </div>
                                            <div className="messages-list">
                                                {chatMessages.length === 0 ? (
                                                    <div className="no-messages">No messages found</div>
                                                ) : (
                                                    chatMessages.map((msg, idx) => (
                                                        <div
                                                            key={msg.id || idx}
                                                            className={`message-bubble ${msg.from_me ? 'sent' : 'received'}`}
                                                        >
                                                            <div className="message-content">
                                                                {msg.body || `[${msg.type}]`}
                                                            </div>
                                                            <div className="message-time">
                                                                {msg.timestamp
                                                                    ? new Date(msg.timestamp).toLocaleString()
                                                                    : ''
                                                                }
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            <div className="quick-reply">
                                                <input
                                                    type="text"
                                                    placeholder="Type a reply..."
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter' && e.target.value) {
                                                            const phone = selectedChat.id.replace('@c.us', '').replace('@s.whatsapp.net', '');
                                                            setSendingMessage(true);
                                                            try {
                                                                const result = await adminService.sendWhatsAppMessage(
                                                                    phone,
                                                                    e.target.value
                                                                );
                                                                if (result.success) {
                                                                    toast.success('Reply sent!');
                                                                    e.target.value = '';
                                                                    // Refresh messages
                                                                    const messagesData = await adminService.getWhatsAppMessages(selectedChat.id, 50);
                                                                    setChatMessages(messagesData.messages || []);
                                                                } else {
                                                                    toast.error('Failed to send reply');
                                                                }
                                                            } catch (error) {
                                                                toast.error('Failed to send reply');
                                                            } finally {
                                                                setSendingMessage(false);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'institutions' && (
                                <div className="admin-institutions">
                                    {/* Create Institution Section */}
                                    <div className="create-institution-section card">
                                        <div className="section-header">
                                            <h3><FaBuilding /> Create New Institution</h3>
                                            <p className="section-subtitle">Add a new school or organization</p>
                                        </div>

                                        <form onSubmit={handleCreateInstitution} className="institution-form">
                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label>Institution Name *</label>
                                                    <input
                                                        name="name"
                                                        value={institutionForm.name}
                                                        onChange={handleInstitutionFormChange}
                                                        placeholder="e.g. Springfield High School"
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label>Institution Code (Optional)</label>
                                                    <input
                                                        name="code"
                                                        value={institutionForm.code}
                                                        onChange={handleInstitutionFormChange}
                                                        placeholder="e.g. SHS001"
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-heading">Admin Account Details</div>
                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label>Admin Email *</label>
                                                    <input
                                                        name="admin_email"
                                                        type="email"
                                                        value={institutionForm.admin_email}
                                                        onChange={handleInstitutionFormChange}
                                                        placeholder="admin@school.com"
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label>Admin Password *</label>
                                                    <input
                                                        name="admin_password"
                                                        type="password"
                                                        value={institutionForm.admin_password}
                                                        onChange={handleInstitutionFormChange}
                                                        placeholder="Min. 6 characters"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label>Admin First Name</label>
                                                    <input
                                                        name="admin_first_name"
                                                        value={institutionForm.admin_first_name}
                                                        onChange={handleInstitutionFormChange}
                                                        placeholder="John"
                                                    />
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label>Admin Last Name</label>
                                                    <input
                                                        name="admin_last_name"
                                                        value={institutionForm.admin_last_name}
                                                        onChange={handleInstitutionFormChange}
                                                        placeholder="Doe"
                                                    />
                                                </div>
                                            </div>

                                            <button type="submit" className="btn-create-coupon">
                                                <FaPlus /> Create Institution
                                            </button>
                                        </form>
                                    </div>

                                    {/* Institutions List */}
                                    <div className="institutions-list-section card">
                                        <h3>Existing Institutions</h3>
                                        <div className="table-responsive">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Code</th>
                                                        <th>Admin Email</th>
                                                        <th>Created At</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {institutions.map(inst => (
                                                        <tr key={inst.id}>
                                                            <td>{inst.name}</td>
                                                            <td><span className="code-badge">{inst.code || '-'}</span></td>
                                                            <td>{inst.admin_email || '-'}</td>
                                                            <td>{new Date(inst.created_at).toLocaleDateString()}</td>
                                                            <td>
                                                                <button
                                                                    className="btn-small btn-danger"
                                                                    onClick={async () => {
                                                                        if (window.confirm('Delete this institution? This cannot be undone.')) {
                                                                            await adminService.deleteInstitution(inst.id);
                                                                            loadData();
                                                                            toast.success('Deleted');
                                                                        }
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {institutions.length === 0 && (
                                                        <tr>
                                                            <td colSpan="5" className="text-center">No institutions found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Bulk Coupon Generator */}
                                    <div className="bulk-coupon-section card">
                                        <div className="section-header">
                                            <h3><FaTicketAlt /> Bulk Coupon Generator</h3>
                                            <p className="section-subtitle">Generate multiple coupons for an institution at once</p>
                                        </div>

                                        <form onSubmit={handleCreateBulkCoupons}>
                                            <div className="form-row">
                                                <div className="form-group-enhanced full-width">
                                                    <label>Select Institution *</label>
                                                    <select
                                                        name="institution_id"
                                                        value={bulkCouponForm.institution_id}
                                                        onChange={handleBulkCouponFormChange}
                                                        required
                                                    >
                                                        <option value="">-- Choose Institution --</option>
                                                        {institutions.map(inst => (
                                                            <option key={inst.id} value={inst.id}>{inst.name} ({inst.code})</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label>Coupon Prefix *</label>
                                                    <input
                                                        name="prefix"
                                                        value={bulkCouponForm.prefix}
                                                        onChange={handleBulkCouponFormChange}
                                                        placeholder="e.g. SCHOOL2024"
                                                        required
                                                    />
                                                    <small className="field-hint">e.g. PREFIX-0001, PREFIX-0002</small>
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label>Count (1-500) *</label>
                                                    <input
                                                        type="number"
                                                        name="count"
                                                        value={bulkCouponForm.count}
                                                        onChange={handleBulkCouponFormChange}
                                                        min="1"
                                                        max="500"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group-enhanced">
                                                    <label>Discount Type</label>
                                                    <select
                                                        name="discount_type"
                                                        value={bulkCouponForm.discount_type}
                                                        onChange={handleBulkCouponFormChange}
                                                    >
                                                        <option value="percent">Percentage (%)</option>
                                                        <option value="fixed_amount">Fixed Amount (€)</option>
                                                    </select>
                                                </div>
                                                <div className="form-group-enhanced">
                                                    <label>Discount Value *</label>
                                                    <input
                                                        type="number"
                                                        name="discount_value"
                                                        value={bulkCouponForm.discount_value}
                                                        onChange={handleBulkCouponFormChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <button type="submit" className="btn-create-coupon">
                                                Generate Coupons
                                            </button>
                                        </form>

                                        {bulkCouponResult && (
                                            <div className="bulk-result-box success">
                                                <h4><FaCheck /> Success!</h4>
                                                <p>Generated {bulkCouponResult.created_count} coupons for {bulkCouponResult.institution_name}</p>
                                                <div className="codes-preview">
                                                    <strong>First 5 codes:</strong>
                                                    <ul>
                                                        {bulkCouponResult.codes.slice(0, 5).map(code => (
                                                            <li key={code}>{code}</li>
                                                        ))}
                                                        {bulkCouponResult.codes.length > 5 && <li>...and {bulkCouponResult.codes.length - 5} more</li>}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'analytics' && (
                                <div className="admin-analytics">
                                    {/* System Health Banner */}
                                    {analyticsData && (
                                        <div className={`analytics-health-banner health-${analyticsData.system_health?.status || 'unknown'}`}>
                                            <div className="health-indicator">
                                                <span className="health-dot"></span>
                                                <strong>System {analyticsData.system_health?.status === 'healthy' ? '🟢 Healthy' : analyticsData.system_health?.status === 'stale' ? '🟡 Stale' : '⚪ No Data'}</strong>
                                            </div>
                                            <div className="health-details">
                                                <span>Last campaign: {analyticsData.system_health?.last_campaign_sent_at ? new Date(analyticsData.system_health.last_campaign_sent_at).toLocaleString() : 'Never'}</span>
                                                <span>Sent today: {analyticsData.system_health?.campaigns_sent_today || 0}</span>
                                            </div>
                                            <button
                                                className="btn-run-campaigns"
                                                onClick={async () => {
                                                    setRunningCampaigns(true);
                                                    setCampaignRunResult(null);
                                                    try {
                                                        const result = await adminService.runCampaigns();
                                                        setCampaignRunResult(result);
                                                        toast.success(`Campaigns complete: ${result.result?.sent || 0} sent`);
                                                        loadData();
                                                    } catch (err) {
                                                        toast.error('Campaign run failed: ' + (err.response?.data?.detail?.message || err.message));
                                                    } finally {
                                                        setRunningCampaigns(false);
                                                    }
                                                }}
                                                disabled={runningCampaigns}
                                            >
                                                <FaPlay style={{ marginRight: '6px' }} />
                                                {runningCampaigns ? 'Running...' : 'Run Campaigns Now'}
                                            </button>
                                        </div>
                                    )}

                                    {/* Run result */}
                                    {campaignRunResult && (
                                        <div className="campaign-run-result">
                                            <FaCheck style={{ color: '#22c55e', marginRight: '8px' }} />
                                            <span>Checked: <strong>{campaignRunResult.result?.checked || 0}</strong></span>
                                            <span>Sent: <strong>{campaignRunResult.result?.sent || 0}</strong></span>
                                            <span>Failed: <strong>{campaignRunResult.result?.failed || 0}</strong></span>
                                        </div>
                                    )}

                                    {/* Stats Cards */}
                                    {analyticsData && (
                                        <div className="analytics-cards-grid">
                                            {/* Campaign Stats */}
                                            <div className="analytics-card">
                                                <div className="analytics-card-icon campaigns"><FaEnvelopeOpenText /></div>
                                                <h3>Campaigns Sent</h3>
                                                <p className="stat-number">{analyticsData.campaign_stats?.all_time || 0}</p>
                                                <div className="stat-details">
                                                    <span>Today: {analyticsData.campaign_stats?.today || 0}</span>
                                                    <span>This week: {analyticsData.campaign_stats?.this_week || 0}</span>
                                                </div>
                                            </div>

                                            {/* Campaign Breakdown */}
                                            <div className="analytics-card">
                                                <div className="analytics-card-icon breakdown"><FaChartBar /></div>
                                                <h3>By Campaign</h3>
                                                <div className="campaign-breakdown">
                                                    {analyticsData.campaign_stats?.breakdown && Object.keys(analyticsData.campaign_stats.breakdown).length > 0 ? (
                                                        Object.entries(analyticsData.campaign_stats.breakdown)
                                                            .sort(([a], [b]) => a.localeCompare(b))
                                                            .map(([id, count]) => (
                                                                <div key={id} className="breakdown-item">
                                                                    <span className="breakdown-label">Campaign {id}</span>
                                                                    <span className="breakdown-value">{count}</span>
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <span className="no-data">No campaigns yet</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* User Funnel */}
                                            <div className="analytics-card">
                                                <div className="analytics-card-icon funnel"><FaUserClock /></div>
                                                <h3>User Funnel</h3>
                                                <div className="campaign-breakdown">
                                                    {analyticsData.user_funnel && Object.entries(analyticsData.user_funnel)
                                                        .sort(([, a], [, b]) => b - a)
                                                        .map(([state, count]) => (
                                                            <div key={state} className="breakdown-item">
                                                                <span className="breakdown-label">{state}</span>
                                                                <span className="breakdown-value">{count}</span>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>

                                            {/* Inactivity */}
                                            <div className="analytics-card">
                                                <div className="analytics-card-icon inactivity"><FaUserClock /></div>
                                                <h3>Activity Breakdown</h3>
                                                <div className="campaign-breakdown">
                                                    <div className="breakdown-item">
                                                        <span className="breakdown-label">🟢 Active (24h)</span>
                                                        <span className="breakdown-value">{analyticsData.inactivity?.active_24h || 0}</span>
                                                    </div>
                                                    <div className="breakdown-item">
                                                        <span className="breakdown-label">🟡 Stale (1-7d)</span>
                                                        <span className="breakdown-value">{analyticsData.inactivity?.stale_1_7d || 0}</span>
                                                    </div>
                                                    <div className="breakdown-item">
                                                        <span className="breakdown-label">🔴 Inactive (7d+)</span>
                                                        <span className="breakdown-value">{analyticsData.inactivity?.inactive_7d_plus || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Campaign History Table */}
                                    <div className="analytics-section">
                                        <div className="analytics-section-header">
                                            <h3>Campaign History</h3>
                                            <select
                                                className="campaign-filter"
                                                value={campaignFilter}
                                                onChange={(e) => {
                                                    setCampaignFilter(e.target.value);
                                                    setCampaignHistoryPage(1);
                                                    loadData();
                                                }}
                                            >
                                                <option value="">All Campaigns</option>
                                                {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'].map(c => (
                                                    <option key={c} value={c}>Campaign {c}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="users-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>User</th>
                                                        <th>Email</th>
                                                        <th>Campaign</th>
                                                        <th>Channel</th>
                                                        <th>Sent At</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {campaignHistory.length > 0 ? (
                                                        campaignHistory.map(c => (
                                                            <tr key={c.id}>
                                                                <td>{c.user_name}</td>
                                                                <td>{c.user_email}</td>
                                                                <td><span className="campaign-badge">Campaign {c.campaign_id}</span></td>
                                                                <td>{c.channel}</td>
                                                                <td>{new Date(c.sent_at).toLocaleString()}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: '#888' }}>No campaign records yet</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {campaignHistoryTotal > 20 && (
                                            <div className="pagination">
                                                <button
                                                    onClick={() => { setCampaignHistoryPage(p => Math.max(1, p - 1)); loadData(); }}
                                                    disabled={campaignHistoryPage === 1}
                                                >Previous</button>
                                                <span>Page {campaignHistoryPage}</span>
                                                <button
                                                    onClick={() => { setCampaignHistoryPage(p => p + 1); loadData(); }}
                                                    disabled={campaignHistory.length < 20}
                                                >Next</button>
                                            </div>
                                        )}
                                    </div>

                                    {/* User Lookup */}
                                    <div className="analytics-section">
                                        <h3>User Lookup</h3>
                                        <div className="user-lookup-row">
                                            <input
                                                type="text"
                                                placeholder="Enter user ID or search in Users tab first..."
                                                value={userLookupId}
                                                onChange={(e) => setUserLookupId(e.target.value)}
                                                className="search-input"
                                            />
                                            <button
                                                className="btn-primary"
                                                onClick={async () => {
                                                    if (!userLookupId.trim()) return;
                                                    try {
                                                        const data = await adminService.getUserAnalytics(userLookupId.trim());
                                                        setUserAnalytics(data);
                                                    } catch (err) {
                                                        toast.error('User not found');
                                                        setUserAnalytics(null);
                                                    }
                                                }}
                                            >
                                                <FaSearch style={{ marginRight: '4px' }} /> Lookup
                                            </button>
                                        </div>

                                        {userAnalytics && (
                                            <div className="user-analytics-result">
                                                <div className="user-analytics-header">
                                                    <h4>{userAnalytics.user.name}</h4>
                                                    <span className="user-email">{userAnalytics.user.email}</span>
                                                </div>
                                                <div className="user-analytics-grid">
                                                    <div className="ua-field">
                                                        <label>State</label>
                                                        <span className={`status-badge ${userAnalytics.user.user_state}`}>{userAnalytics.user.user_state || 'N/A'}</span>
                                                    </div>
                                                    <div className="ua-field">
                                                        <label>Last Active</label>
                                                        <span>{userAnalytics.user.last_active_at ? new Date(userAnalytics.user.last_active_at).toLocaleString() : 'Never'}</span>
                                                    </div>
                                                    <div className="ua-field">
                                                        <label>Verified</label>
                                                        <span>{userAnalytics.user.is_verified ? '✅ Yes' : '❌ No'}</span>
                                                    </div>
                                                    <div className="ua-field">
                                                        <label>Created</label>
                                                        <span>{userAnalytics.user.created_at ? new Date(userAnalytics.user.created_at).toLocaleDateString() : 'N/A'}</span>
                                                    </div>
                                                    <div className="ua-field">
                                                        <label>Source</label>
                                                        <span>{userAnalytics.user.traffic_source || 'N/A'}</span>
                                                    </div>
                                                    <div className="ua-field">
                                                        <label>UTM</label>
                                                        <span>{[userAnalytics.user.utm_source, userAnalytics.user.utm_medium, userAnalytics.user.utm_campaign].filter(Boolean).join(' / ') || 'N/A'}</span>
                                                    </div>
                                                </div>
                                                {userAnalytics.subscription && (
                                                    <div className="ua-subscription">
                                                        <strong>Subscription:</strong> {userAnalytics.subscription.type} ({userAnalytics.subscription.status}) — {new Date(userAnalytics.subscription.start_date).toLocaleDateString()} to {new Date(userAnalytics.subscription.end_date).toLocaleDateString()}
                                                    </div>
                                                )}
                                                {userAnalytics.campaigns.length > 0 && (
                                                    <div className="ua-campaigns">
                                                        <strong>Campaign History:</strong>
                                                        <ul>
                                                            {userAnalytics.campaigns.map((c, i) => (
                                                                <li key={i}>Campaign {c.campaign_id} via {c.channel} — {new Date(c.sent_at).toLocaleString()}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </>
                    )}
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default AdminDashboard;
